import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Menu, MenuItem, Snackbar, Alert,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { ButtonOutlined } from '../../UI';
import { downloadFichaFinanceira } from '../../../../services/core/processos/processosFichaFinanceira';
import { openPageFileBase64, downloadFileSispag } from '../../../../helpers/utils/utils';

/**
 * `downloadFichaFinanceira(idProcesso, activeTab, ordenacao)` decide o
 * relatório por `activeTab === 1 ? 'sintetica' : 'analitica'` — qualquer
 * valor diferente de `1` já resolve para a visão Analítica, que é a que
 * contém as colunas pedidas pela AC desta etapa (Manual, Data, Acordo,
 * Parcela, Título, Principal Cliente, Multa, Protesto, Juros Cliente,
 * Comissão, HB, Negociador, DSR, Comercial, Valor Pago).
 */
const ACTIVE_TAB_ANALITICA = 0;
/** Mesmo default já usado pela Ficha Financeira real para a visão Analítica. */
const ORDENACAO_PADRAO = ['dataLancamento,DESC'];
const MENSAGEM_ERRO_DEMONSTRATIVO = 'Não foi possível gerar o Demonstrativo por Título.';

/**
 * Botão "Demonstrativo por Título" (AW2-1079 Etapa 4) — reaproveita o mesmo
 * relatório Analítico já existente em produção (`downloadFichaFinanceira`,
 * `services/core/processos/processosFichaFinanceira.js`), só que com 2
 * opções que nenhuma tela do sistema oferece juntas hoje: Visualizar
 * (`openPageFileBase64`) e Baixar (`downloadFileSispag`) — ambos utils puros
 * já existentes em `helpers/utils/utils.js`, nenhum helper novo necessário.
 *
 * `OperacoesFichaFinanceira.jsx` nunca desmonta ao trocar de processo
 * selecionado na grid (mesmo padrão de `AbaAcompanhamento.jsx` — `renderAba`
 * de `ControleProcessosPage.jsx` não usa `key` por processo), então uma
 * resposta tardia de um `idProcesso` anterior precisa ser descartada: guard
 * `idProcessoRef` (padrão "latest ref", mutado direto no corpo do
 * componente) + reset síncrono de `carregando`/`erro` no efeito keyed por
 * `idProcesso`, mesmo padrão já documentado em `src/prototipo/CLAUDE.md`
 * para `onClickDemonstrativoValores`/`onClickSimularQuitacao`
 * (`AbaAcompanhamento.jsx`).
 */
export default function MenuDemonstrativoPorTitulos({ idProcesso }) {
  const [anchor, setAnchor] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  useEffect(() => {
    setCarregando(false);
    setErro('');
  }, [idProcesso]);

  async function buscarRelatorio() {
    const idProcessoAlvo = idProcesso;
    setCarregando(true);
    try {
      const { data, headers } = await downloadFichaFinanceira(
        idProcessoAlvo,
        ACTIVE_TAB_ANALITICA,
        ORDENACAO_PADRAO,
      );
      if (idProcessoRef.current !== idProcessoAlvo || !(data?.length > 0)) {
        return null;
      }
      return { data, contentType: headers['content-type'] };
    } catch {
      if (idProcessoRef.current === idProcessoAlvo) {
        setErro(MENSAGEM_ERRO_DEMONSTRATIVO);
      }
      return null;
    } finally {
      if (idProcessoRef.current === idProcessoAlvo) {
        setCarregando(false);
      }
    }
  }

  async function onVisualizar() {
    setAnchor(null);
    const relatorio = await buscarRelatorio();
    if (relatorio) {
      openPageFileBase64(relatorio.data, relatorio.contentType);
    }
  }

  async function onBaixar() {
    setAnchor(null);
    const relatorio = await buscarRelatorio();
    if (relatorio) {
      downloadFileSispag(`demonstrativo-titulos-processo-${idProcesso}.pdf`, relatorio.data);
    }
  }

  return (
    <>
      <ButtonOutlined
        startIcon={<PersonOutlineIcon />}
        disabled={!idProcesso || carregando}
        onClick={event => setAnchor(event.currentTarget)}
      >
        DEMONSTRATIVO POR TÍTULO
      </ButtonOutlined>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={onVisualizar}>Visualizar</MenuItem>
        <MenuItem onClick={onBaixar}>Baixar</MenuItem>
      </Menu>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}
MenuDemonstrativoPorTitulos.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
MenuDemonstrativoPorTitulos.defaultProps = {
  idProcesso: null,
};
